import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  ChassisHeightFilterValue,
  ChassisHeightForm,
  ChassisHeightListMeta,
  ChassisHeightRow,
  ChassisHeightYearFilter,
} from "../types";
import { initialChassisHeightForm, initialState } from "./initialState";

export const sliceKey = "chassisHeightState";

type ChassisHeightListSuccessPayload = {
  data: ChassisHeightRow[];
  meta: ChassisHeightListMeta;
};

const chassisHeightSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getChassisHeightList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getChassisHeightListSuccess(
      state,
      action: PayloadAction<ChassisHeightListSuccessPayload>
    ) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getChassisHeightListError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(
      state,
      action: PayloadAction<ChassisHeightListSuccessPayload>
    ) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    setManufacturerFilter(
      state,
      action: PayloadAction<ChassisHeightFilterValue>
    ) {
      state.manufacturerFilter = action.payload;
    },

    setSuspensionTypeFilter(
      state,
      action: PayloadAction<ChassisHeightFilterValue>
    ) {
      state.suspensionTypeFilter = action.payload;
    },

    setMasterModelFilter(
      state,
      action: PayloadAction<ChassisHeightFilterValue>
    ) {
      state.masterModelFilter = action.payload;
    },

    setYearFilter(state, action: PayloadAction<ChassisHeightYearFilter>) {
      state.yearFilter = action.payload;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.error = null;
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.saving = false;
      state.error = null;
      state.form = initialChassisHeightForm;
    },

    setFormField(
      state,
      action: PayloadAction<{
        name: keyof ChassisHeightForm;
        value: string;
      }>
    ) {
      const { name, value } = action.payload;
      state.form[name] = value;
    },

    resetForm(state) {
      state.form = initialChassisHeightForm;
    },

    addChassisHeight(state) {
      state.saving = true;
      state.error = null;
    },

    addChassisHeightSuccess(state) {
      state.saving = false;
      state.isAddModalOpen = false;
      state.form = initialChassisHeightForm;
      state.error = null;
    },

    addChassisHeightError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
  },
});

export const { actions: chassisHeightActions, reducer } = chassisHeightSlice;
